import { onPageVisibility } from '@apparel/core/events/onPageVisibility';
import { AxiosInstance, AxiosRequestConfig } from 'axios';
import { Base64 } from 'js-base64';
import { orderBy } from 'lodash';
import { APIService } from '../api';
import { AuthService } from '../auth';
import { unix } from '../date-time';
import { EventEmitter } from '../events';
import { ChatMessage } from '../models/ChatMessage';
import { ChatMessageStatus } from '../models/ChatMessageStatus';
import { ChatRoom } from '../models/ChatRoom';
import { SoundManager, Sounds } from '../ui/sounds';
import { ChatConfig, DefaultChatConfig } from './ChatConfig';
import { ChatEvents } from './ChatEvents';

export class ChatService {
    private static __SELF__: ChatService;
    private socket!: WebSocket | null;

    private emitter = new EventEmitter<ChatEvents>();
    private timeoutRetry = 5000;
    private retries = 0;
    private timeoutAddPercent = 0.6;
    private auth: AuthService;
    private endpoint: AxiosInstance;
    public config: Partial<ChatConfig> = DefaultChatConfig;
    private isPageFocused = true;

    constructor() {
        this.auth = AuthService.getInstance();
        this.endpoint = APIService.createLambdaEndpoint('/chat', { baseURL: this.endpointUrl });

        this.endpoint.interceptors.request.use((config) => {
            config.params = config.params || {};
            config.params.platform = this.config.platform;
            return config;
        });

        this.handleOpen = this.handleOpen.bind(this);
        this.handleError = this.handleError.bind(this);
        this.handleMessage = this.handleMessage.bind(this);
        this.handleClose = this.handleClose.bind(this);

        this.readMessageOfRoom = this.readMessageOfRoom.bind(this);
    }

    get endpointUrl() {
        if (!!process.env.REACT_APP_CHAT_ENDPOINT) {
            return process.env.REACT_APP_CHAT_ENDPOINT;
        } else if (!!process.env.CHAT_ENDPOINT) {
            return process.env.CHAT_ENDPOINT;
        }

        return undefined;
    }

    public static getInstance(): ChatService {
        if (!ChatService.__SELF__) {
            ChatService.__SELF__ = new ChatService();
        }

        return ChatService.__SELF__;
    }

    public connect(): WebSocket {
        if (this.socket) {
            return this.socket;
        }

        let endpoint: undefined | string;

        if (!!process.env.REACT_APP_CHAT_WS_ENDPOINT) {
            endpoint = process.env.REACT_APP_CHAT_WS_ENDPOINT;
        } else if (!!process.env.CHAT_WS_ENDPOINT) {
            endpoint = process.env.CHAT_WS_ENDPOINT;
        }

        this.socket = APIService.createWebsocket(endpoint, {
            platform: this.config.platform,
        });

        this.initDefaultEvents();

        return this.socket;
    }

    public reset() {
        this.emitter.reset();
        this.socket?.removeEventListener('open', this.handleOpen);
        this.socket?.removeEventListener('error', this.handleError);
        this.socket?.removeEventListener('message', this.handleMessage);
        this.socket?.removeEventListener('close', this.handleClose);
    }

    private initDefaultEvents() {
        this.reset();
        this.socket?.removeEventListener('close', this.handleClose);

        this.emitter.on('reconnect', () => {
            if (this.retries >= 5) {
                return;
            }

            this.socket?.close();

            const timeout = Math.floor(this.timeoutRetry + this.timeoutRetry * this.retries * this.timeoutAddPercent);
            this.emitter.emit(ChatEvents.RETRY, timeout);
            setTimeout(() => this.connect(), timeout);
            this.retries++;
        });

        this.socket?.addEventListener('open', this.handleOpen);
        this.socket?.addEventListener('error', this.handleError);
        this.socket?.addEventListener('message', this.handleMessage);
        this.socket?.addEventListener('close', this.handleClose);

        onPageVisibility((hidden) => {
            this.isPageFocused = !hidden;
        });
    }

    public close() {
        this.socket?.close();
        this.reset();
        this.socket = null;
    }

    private handleOpen() {
        this.emitter.emit(ChatEvents.CONNECT);
    }

    private handleError(event: Event) {
        this.emitter.emit(ChatEvents.ERROR, event);
        this.emitter.emit('reconnect', event);
    }

    private handleMessage(message: MessageEvent) {
        let data: any;

        try {
            data = JSON.parse(message.data);
        } catch (e) {
            return;
        }

        if (data) {
            this.emitter.emit(ChatEvents.MESSAGE, data, message);

            const action = data.action || data.type;
            if (action) {
                if (action === ChatEvents.NEW_MESSAGE && !this.isPageFocused) {
                    SoundManager.play(Sounds.NEW_MESSAGE);
                }
                this.emitter.emit(action, data.payload || data.data);
            }
        }
    }

    private handleClose(event: Event) {
        this.emitter.emit(ChatEvents.DISCONNECT, event);
    }

    public on(event: ChatEvents, callback: (...args: any) => void) {
        return this.emitter.on(event, callback);
    }

    public getMessagesByRoomID(roomID: number) {
        return this.endpoint.get(`rooms/${roomID}/messages`);
    }

    public async getRooms(config?: AxiosRequestConfig) {
        const { data } = await this.endpoint.get<ChatRoom[]>('rooms', config);
        const rooms = (data ?? []).map((item) => new ChatRoom().from(item));

        return orderBy(
            rooms,
            (room) => {
                const datetime = room?.last_message?.created_at;
                return datetime ? unix(datetime) : 0;
            },
            'desc',
        );
    }

    public async postMessage({ message, format, extra, room_id }: Partial<ChatMessage>): Promise<ChatMessage> {
        const { data } = await this.endpoint.post(`rooms/${room_id}/messages`, {
            message,
            format,
            extra,
        });

        return new ChatMessage().from(data.message);
    }

    public async postRoom(members: number[]): Promise<ChatRoom> {
        const { data } = await this.endpoint.post('rooms', { members });

        return new ChatRoom().from(data);
    }

    public configure(config: Partial<ChatConfig> = {}) {
        this.config = { ...DefaultChatConfig, ...this.config, ...config };
        return this;
    }

    syncQueue: any[] = [];
    runningQueue = false;

    private async consumeQueue() {
        if (this.runningQueue) {
            return;
        }

        this.runningQueue = true;

        let length = this.syncQueue.length;
        while (length > 0) {
            const { value } = this.syncQueue.shift();
            try {
                await value();
            } catch (e) {
                // IDC -> I don't care
            }

            length--;
        }

        this.runningQueue = false;
    }

    public readMessageOfRoom(messageId: number, roomId: number) {
        const key = `rooms/${roomId}/messages/${messageId}`;
        this.syncQueue.push({
            key,
            value: () =>
                this.endpoint.put(key, {
                    status: ChatMessageStatus.READ,
                }),
        });

        setTimeout(() => {
            // noinspection JSIgnoredPromiseFromCall
            this.consumeQueue();
        }, 100);
    }

    public createNewConversationURL(
        userId: number,
        additionalData?: Record<string, string | number | boolean | null>,
        members: number[] = [],
    ) {
        if (!userId) {
            return null;
        }

        const data = Base64.encode(
            JSON.stringify({
                ...(additionalData ?? {}),
                members: [userId, ...members],
            }),
            true,
        );

        return `/platform/conversations/${data}/new`;
    }

    public async sendUserTyping(roomId: number, typing: boolean) {
        const { data } = await this.endpoint.post(`/rooms/${roomId}/typing`, { typing });
        return data;
    }
}
